'use client'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowRight,
  faCirclePlay,
  faFileLines,
  faTruckFast,
  faUserCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'

import { LINKS } from './content'
import { Inner, Section, SectionBody, SectionTitle, TextLink } from './ui'

type ResourceLink =
  | 'howToRegister'
  | 'resaleCertificates'
  | 'browsingTips'
  | 'inventoryTypes'
  | 'paymentMethods'
  | 'shippingMethods'
  | 'receivingInventory'

type Step = {
  key: 'register' | 'browse' | 'pay'
  bullets: string[]
  icon: IconDefinition
  links: Array<{ key: ResourceLink; href: string }>
}

const STEPS: Step[] = [
  {
    key: 'register',
    bullets: [
      'LoggedOutHomepage.howItWorks.steps.register.bullet1',
      'LoggedOutHomepage.howItWorks.steps.register.bullet2',
      'LoggedOutHomepage.howItWorks.steps.register.bullet3',
    ],
    icon: faUserCheck,
    links: [
      { key: 'howToRegister', href: LINKS.howToRegister },
      { key: 'resaleCertificates', href: LINKS.resaleCertificates },
    ],
  },
  {
    key: 'browse',
    bullets: [
      'LoggedOutHomepage.howItWorks.steps.browse.bullet1',
      'LoggedOutHomepage.howItWorks.steps.browse.bullet2',
    ],
    icon: faFileLines,
    links: [
      { key: 'browsingTips', href: LINKS.browsingTips },
      { key: 'inventoryTypes', href: LINKS.inventoryTypes },
    ],
  },
  {
    key: 'pay',
    bullets: [
      'LoggedOutHomepage.howItWorks.steps.pay.bullet1',
      'LoggedOutHomepage.howItWorks.steps.pay.bullet2',
      'LoggedOutHomepage.howItWorks.steps.pay.bullet3',
      'LoggedOutHomepage.howItWorks.steps.pay.bullet4',
    ],
    icon: faTruckFast,
    links: [
      { key: 'paymentMethods', href: LINKS.paymentMethods },
      { key: 'shippingMethods', href: LINKS.shippingMethods },
      { key: 'receivingInventory', href: LINKS.receivingInventory },
    ],
  },
]

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media ${Breakpoints.min.large} {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`

const HeaderLinks = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
`

const Cards = styled.ol`
  display: grid;
  gap: 1.5rem;
  margin: 0;
  padding: 0;
  list-style: none;

  @media ${Breakpoints.min.large} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

const Card = styled.li`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 0.75rem;
  background: ${Colors.Semantic.Neutral.white};
  ${Shadows.MultipleCards};
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StepNumber = styled.span`
  ${Typography.Subtitle1};
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${Colors.Semantic.BStock.backgroundSelected};
  color: ${Colors.Semantic.BStock.default.lightBackground};
`

const CardTitle = styled.h3`
  ${Typography.Title3};
  margin: 0;
  color: ${Colors.Semantic.Neutral.light_black};
`

const Bullets = styled.ul`
  ${Typography.Body2};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding-left: 1.25rem;
  color: ${Colors.Semantic.Neutral.dark_grey};

  & > li {
    list-style: disc;
  }
`

const CardFooter = styled.div`
  ${Typography.Body3};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.light_grey};
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export default function HowBuyingWorks() {
  return (
    <Section
      $background={Colors.Semantic.Neutral.light_grey}
      data-testid="logged-out-how-buying-works"
    >
      <Inner>
        <Header>
          <div>
            <SectionTitle>
              <FormattedMessage id="LoggedOutHomepage.howItWorks.title" />
            </SectionTitle>
            <SectionBody>
              <FormattedMessage id="LoggedOutHomepage.howItWorks.intro" />
            </SectionBody>
          </div>
          <HeaderLinks>
            <TextLink href={LINKS.whyBstock}>
              <FontAwesomeIcon icon={faCirclePlay} />
              <FormattedMessage id="LoggedOutHomepage.howItWorks.watch" />
              <FontAwesomeIcon icon={faArrowRight} />
            </TextLink>
            <TextLink href={LINKS.howItWorks}>
              <FormattedMessage id="LoggedOutHomepage.howItWorks.seeInFull" />
              <FontAwesomeIcon icon={faArrowRight} />
            </TextLink>
          </HeaderLinks>
        </Header>
        <Cards>
          {STEPS.map((step, index) => (
            <Card key={step.key}>
              <CardHeader>
                <StepNumber>{index + 1}</StepNumber>
                <CardTitle>
                  <FormattedMessage
                    id={`LoggedOutHomepage.howItWorks.steps.${step.key}.title`}
                  />
                </CardTitle>
              </CardHeader>
              <Bullets>
                {step.bullets.map((id) => (
                  <li key={id}>
                    <FormattedMessage id={id} />
                  </li>
                ))}
              </Bullets>
              <CardFooter>
                <FontAwesomeIcon icon={step.icon} />
                <FormattedMessage
                  id={`LoggedOutHomepage.howItWorks.steps.${step.key}.meta`}
                />
                {step.links.map((link) => (
                  <TextLink key={link.key} href={link.href}>
                    <FormattedMessage
                      id={`LoggedOutHomepage.howItWorks.links.${link.key}`}
                    />
                    <FontAwesomeIcon icon={faArrowRight} />
                  </TextLink>
                ))}
              </CardFooter>
            </Card>
          ))}
        </Cards>
      </Inner>
    </Section>
  )
}
